import React, { useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import log from 'loglevel';
import { Link2, MoreHorizontal, Send, UserCog, UserMinus, UserPlus, Users } from 'lucide-react';

import { useToast } from '@admin-platform/ui/components';
import Plus from '@admin-platform/ui/icons/outline/plus.icon.svg?react';

import { Badge } from '../../../components/Badge/Badge';
import { Button } from '../../../components/Button/Button';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import { ErrorDisplay } from '../../../components/ErrorDisplay/ErrorDisplay';
import { FloatingPopover } from '../../../components/FloatingPopover/FloatingPopover';
import { Skeleton } from '../../../components/Skeleton/Skeleton';
import {
  Table2,
  TableBody,
  TableCell,
  TableHeaderRow,
  TableRow,
  TableShell,
  TableTitleBar,
} from '../../../components/Table/Table2';
import { parseGraphQLErrorForUser } from '../../../utils/normalizeGraphqlError';

import type {
  GetTenantUsersQuery,
  GetTenantUsersQueryVariables,
} from './queries/tenantUsers.query.graphql';
import GET_TENANT_USERS from './queries/tenantUsers.query.graphql';

import type { UpdateTenantUserMutationVariables } from './mutations/updateTenantUser.mutation.graphql';
import UPDATE_TENANT_USER from './mutations/updateTenantUser.mutation.graphql';

import type {
  ResendTenantUserInviteMutation,
  ResendTenantUserInviteMutationVariables,
} from './mutations/resendTenantUserInvite.mutation.graphql';
import RESEND_TENANT_USER_INVITE from './mutations/resendTenantUserInvite.mutation.graphql';

import type {
  TenantUserInvitationLinkMutation,
  TenantUserInvitationLinkMutationVariables,
} from './mutations/tenantUserInvitationLink.mutation.graphql';
import TENANT_USER_INVITATION_LINK from './mutations/tenantUserInvitationLink.mutation.graphql';

import { ChangeRoleModal } from './ChangeRoleModal';
import { InviteUserModal } from './InviteUserModal';
import { useIsTenantAdmin } from '../../../hooks/useIsTenantAdmin';
import SEAT_USAGE from '../../Settings/Seats/queries/seatUsage.query.graphql';
import type { SeatUsageQuery } from '../../Settings/Seats/queries/seatUsage.query.graphql';

import styles from './TeamMembers.module.css';

type TenantUser = GetTenantUsersQuery['tenantUsers'][0];

export default function TeamMembers(): React.ReactElement {
  const toast = useToast();
  // Presentation only: the API refuses every write from a non-admin, so a member
  // is shown the list without the actions that would just fail for them.
  const isAdmin = useIsTenantAdmin();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState<TenantUser | null>(null);
  const [userToReactivate, setUserToReactivate] = useState<TenantUser | null>(null);
  const [userToChangeRole, setUserToChangeRole] = useState<TenantUser | null>(null);

  const { data, loading, error, refetch } = useQuery<
    GetTenantUsersQuery,
    GetTenantUsersQueryVariables
  >(GET_TENANT_USERS, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  // Seats gate inviting, so the screen that invites has to know about them.
  const { data: seatData, refetch: refetchSeats } = useQuery<SeatUsageQuery>(SEAT_USAGE, {
    fetchPolicy: 'cache-and-network',
  });

  const [updateTenantUser, { loading: updateLoading }] = useMutation<
    unknown,
    UpdateTenantUserMutationVariables
  >(UPDATE_TENANT_USER, {
    onCompleted: () => {
      refetch();
      // Deactivating frees a seat and reactivating takes one: the count and
      // the Invite button have to follow.
      void refetchSeats();
      toast('User updated successfully', 'success');
    },
    onError: (err) => {
      log.error('Error updating user:', err);
      const userFriendlyMessage = parseGraphQLErrorForUser(err);
      toast(userFriendlyMessage, 'error');
    },
  });

  const [resendInvite, { loading: resendLoading }] = useMutation<
    ResendTenantUserInviteMutation,
    ResendTenantUserInviteMutationVariables
  >(RESEND_TENANT_USER_INVITE, {
    onCompleted: (data) => {
      // A resolved mutation is not the same as a sent email: the resolver
      // returns `false` rather than throwing when SES itself fails, so
      // `onCompleted` alone can't tell success from a swallowed failure.
      if (data.resendTenantUserInvite) {
        toast('Invitation resent', 'success');
      } else {
        toast('Could not send the invitation email. Try again shortly.', 'error');
      }
    },
    onError: (err) => {
      log.error('Error resending invite:', err);
      toast(parseGraphQLErrorForUser(err), 'error');
    },
  });

  const [getInvitationLink, { loading: copyLinkLoading }] = useMutation<
    TenantUserInvitationLinkMutation,
    TenantUserInvitationLinkMutationVariables
  >(TENANT_USER_INVITATION_LINK);

  const handleResendInvite = async (user: TenantUser) => {
    await resendInvite({ variables: { id: user.id } }).catch(() => undefined);
  };

  const handleCopyInvitationLink = async (user: TenantUser) => {
    try {
      const { data: linkData } = await getInvitationLink({ variables: { id: user.id } });
      const link = linkData?.tenantUserInvitationLink;
      if (!link) return;
      await navigator.clipboard.writeText(link);
      toast('Invitation link copied to clipboard', 'success');
    } catch (err) {
      log.error('Error getting invitation link:', err);
      toast(parseGraphQLErrorForUser(err), 'error');
    }
  };

  const handleInviteSuccess = () => {
    setIsInviteModalOpen(false);
    refetch();
    void refetchSeats();
  };

  const handleDeactivateUser = async () => {
    if (!userToDeactivate) return;

    await updateTenantUser({
      variables: {
        input: {
          id: userToDeactivate.id,
          isActive: false,
        },
      },
    }).catch(() => undefined);
    setUserToDeactivate(null);
  };

  const handleReactivateUser = async () => {
    if (!userToReactivate) return;

    await updateTenantUser({
      variables: {
        input: {
          id: userToReactivate.id,
          isActive: true,
        },
      },
    }).catch(() => undefined);
    setUserToReactivate(null);
  };

  const formatDate = (dateString: string | Date | null | undefined) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const getRoleBadge = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
        return (
          <Badge badgeType="outline" indicator="blue">
            Admin
          </Badge>
        );
      case 'member':
        return <Badge badgeType="outline">Member</Badge>;
      default:
        return <Badge badgeType="outline">{role}</Badge>;
    }
  };

  /**
   * Nobody has taken any action on this membership since it was invited —
   * the same signal used to decide whether resending or copying the link is
   * even meaningful (once a person has logged in, the setup step is done).
   */
  const isPending = (user: TenantUser) => !user.lastAccessedAt;

  const getStatusBadge = (user: TenantUser) => {
    if (!user.isActive) {
      return (
        <Badge badgeType="outline" indicator="red">
          Inactive
        </Badge>
      );
    }
    if (isPending(user)) {
      return (
        <Badge badgeType="outline" indicator="orange">
          Invited
        </Badge>
      );
    }
    return (
      <Badge badgeType="outline" indicator="green">
        Active
      </Badge>
    );
  };

  /**
   * Get display name for a user
   * Prioritizes: user.name > user.firstName + lastName > email > userId
   */
  const getUserDisplayName = (user: TenantUser): string => {
    if (user.user?.name) return user.user.name;
    if (user.user?.firstName && user.user?.lastName) {
      return `${user.user.firstName} ${user.user.lastName}`;
    }
    if (user.user?.firstName) return user.user.firstName;
    if (user.user?.email) return user.user.email;
    if (user.email) return user.email;
    // Fallback to userId but truncate if it's a UUID
    if (user.userId.length > 20) {
      return `${user.userId.substring(0, 8)}...`;
    }
    return user.userId;
  };

  /**
   * Get email for display (secondary info)
   */
  const getUserEmail = (user: TenantUser): string | null => {
    return user.user?.email || user.email || null;
  };

  /** Row menu, shared by the mobile card and the desktop table. */
  const renderRowActions = (user: TenantUser) => (
    <>
      <button onClick={() => setUserToChangeRole(user)}>
        <UserCog />
        Change Role
      </button>
      {isPending(user) && (
        <>
          <button disabled={resendLoading} onClick={() => void handleResendInvite(user)}>
            <Send />
            Resend Invite
          </button>
          <button disabled={copyLinkLoading} onClick={() => void handleCopyInvitationLink(user)}>
            <Link2 />
            Copy Invitation Link
          </button>
        </>
      )}
      {user.isActive ? (
        <button onClick={() => setUserToDeactivate(user)}>
          <UserMinus />
          Deactivate
        </button>
      ) : (
        <button onClick={() => setUserToReactivate(user)}>
          <UserPlus />
          Reactivate
        </button>
      )}
    </>
  );

  // Loading state
  if (loading) {
    return (
      <div className={styles.Container}>
        <div className={styles.SkeletonCard}>
          <div className={styles.SkeletonTableHeader}>
            <Skeleton className={styles.SkeletonTableTitle} />
          </div>
          <div className={styles.SkeletonTableContent}>
            <div className={styles.SkeletonTableRows}>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className={styles.SkeletonTableRow} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    log.error('GraphQL Error:', error);
    return (
      <div className={styles.Container}>
        <ErrorDisplay
          error={error}
          message="Failed to load team members. Please try again later."
          onRetry={() => {
            void refetch();
          }}
        />
      </div>
    );
  }

  const users = data?.tenantUsers || [];

  // Null limit means unlimited, which is every tenant until Revvly sets one.
  const seatUsage = seatData?.seatUsage;
  const noSeatsLeft =
    !!seatUsage &&
    seatUsage.limit !== null &&
    seatUsage.limit !== undefined &&
    (seatUsage.remaining ?? 0) <= 0;

  return (
    <div className={styles.Container}>
      <TableShell>
        <TableTitleBar title="Team Members" count={users.length}>
          {/* The seat count sits next to the invite button because that is
              where it matters: inviting is what consumes one, and finding out
              at the error is too late. */}
          {seatUsage && seatUsage.limit !== null && seatUsage.limit !== undefined && (
            <span className={styles.SeatCount}>
              {seatUsage.used} of {seatUsage.limit} seats used
            </span>
          )}
          {isAdmin && (
            <Button
              variant="primary"
              onClick={() => setIsInviteModalOpen(true)}
              disabled={noSeatsLeft}
              title={
                noSeatsLeft
                  ? 'Every seat is in use. Free one by deactivating someone, or request more in Settings → Seats.'
                  : undefined
              }
            >
              <Plus className={styles.ButtonIcon} />
              Invite User
            </Button>
          )}
        </TableTitleBar>
        {users.length === 0 ? (
          <div className={styles.EmptyState}>
            <div className={styles.EmptyStateIcon}>
              <Users />
            </div>
            <h3 className={styles.EmptyStateTitle}>No team members yet</h3>
            <p className={styles.EmptyStateDescription}>
              Invite team members to collaborate on this tenant. They&apos;ll receive an email
              invitation to join.
            </p>
            {isAdmin && (
              <Button variant="primary" onClick={() => setIsInviteModalOpen(true)}>
                <UserPlus className={styles.ButtonIcon} />
                Invite First Member
              </Button>
            )}
          </div>
        ) : (
          <React.Fragment>
            {/* Mobile Cards View */}
            <div className={styles.MobileCards}>
              {users.map((user) => (
                <div key={user.id} className={styles.MobileCard}>
                  <div className={styles.MobileCardHeader}>
                    <div>
                      <div className={styles.MobileCardTitle}>{getUserDisplayName(user)}</div>
                      <div className={styles.MobileCardSubtitle}>
                        {getUserEmail(user) || `ID: ${user.id}`}
                      </div>
                    </div>
                    {isAdmin && (
                      <FloatingPopover placement="top" offset={8} content={renderRowActions(user)}>
                        <button type="button" className={styles.DropdownMenuTrigger}>
                          <MoreHorizontal className={styles.DropdownIcon} />
                        </button>
                      </FloatingPopover>
                    )}
                  </div>
                  <div className={styles.MobileCardGrid}>
                    <div className={styles.MobileCardRow}>
                      <span className={styles.MobileCardLabel}>Role:</span>
                      {getRoleBadge(user.role)}
                    </div>
                    <div className={styles.MobileCardRow}>
                      <span className={styles.MobileCardLabel}>Status:</span>
                      {getStatusBadge(user)}
                    </div>
                    <div className={styles.MobileCardRow}>
                      <span className={styles.MobileCardLabel}>Last Access:</span>
                      <span>{formatDate(user.lastAccessedAt)}</span>
                    </div>
                    <div className={styles.MobileCardRow}>
                      <span className={styles.MobileCardLabel}>Invited:</span>
                      <span>{formatDate(user.invitedAt)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className={styles.DesktopTable}>
              <Table2>
                <TableHeaderRow
                  headers={['User', 'Role', 'Status', 'Last Access', 'Invited', '']}
                />
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className={styles.UserCell}>
                          <span className={styles.UserEmail}>{getUserDisplayName(user)}</span>
                          <span className={styles.UserId}>
                            {getUserEmail(user) || `ID: ${user.id}`}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{getRoleBadge(user.role)}</TableCell>
                      <TableCell>{getStatusBadge(user)}</TableCell>
                      <TableCell>{formatDate(user.lastAccessedAt)}</TableCell>
                      <TableCell>{formatDate(user.invitedAt)}</TableCell>
                      <TableCell>
                        {isAdmin && (
                          <FloatingPopover
                            placement="bottom"
                            offset={8}
                            content={renderRowActions(user)}
                          >
                            <button type="button" className={styles.DropdownMenuTrigger}>
                              <MoreHorizontal className={styles.DropdownIcon} />
                            </button>
                          </FloatingPopover>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table2>
            </div>
          </React.Fragment>
        )}
      </TableShell>

      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onSuccess={handleInviteSuccess}
      />

      <ChangeRoleModal
        user={
          userToChangeRole
            ? {
                id: userToChangeRole.id,
                role: userToChangeRole.role,
                displayName: getUserDisplayName(userToChangeRole),
              }
            : null
        }
        onClose={() => setUserToChangeRole(null)}
        onSuccess={() => {
          setUserToChangeRole(null);
          void refetch();
        }}
      />

      {userToDeactivate && (
        <ConfirmationModal onClose={() => setUserToDeactivate(null)}>
          <ConfirmationModal.Body>
            Are you sure you want to deactivate{' '}
            <strong>{getUserDisplayName(userToDeactivate)}</strong>? They will no longer be able to
            access this tenant.
          </ConfirmationModal.Body>
          <ConfirmationModal.Actions>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setUserToDeactivate(null)}
              disabled={updateLoading}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleDeactivateUser}
              disabled={updateLoading}
            >
              {updateLoading ? 'Deactivating...' : 'Deactivate'}
            </Button>
          </ConfirmationModal.Actions>
        </ConfirmationModal>
      )}

      {userToReactivate && (
        <ConfirmationModal onClose={() => setUserToReactivate(null)}>
          <ConfirmationModal.Body>
            Are you sure you want to reactivate{' '}
            <strong>{getUserDisplayName(userToReactivate)}</strong>? They will be able to access
            this tenant again.
          </ConfirmationModal.Body>
          <ConfirmationModal.Actions>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setUserToReactivate(null)}
              disabled={updateLoading}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={handleReactivateUser}
              disabled={updateLoading}
            >
              {updateLoading ? 'Reactivating...' : 'Reactivate'}
            </Button>
          </ConfirmationModal.Actions>
        </ConfirmationModal>
      )}
    </div>
  );
}
